/* Modulo: Product Quick Buy — cart add + add-then-checkout for a product grid.
   Vanilla, no deps. Adapted from extensions/modulo-theme/assets/modulo-block-bundle.js
   (NOT from the section siblings — those carry an r.ok gap, a per-button
   in-flight flag, and a 3-way drawer probe that no-ops on Horizon). */
(function () {
  'use strict';
  if (typeof window === 'undefined') return;

  /* Markets: never hardcode '/checkout'. There is no routes.checkout_url, and
     window.Shopify.routes.root is the only value documented as always ending
     in a '/'. On a Nordic subfolder market it is '/en-se/', and a literal
     '/checkout' throws away the shopper's currency — invisible on a US primary
     market, which is exactly why it ships. */
  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /* fetch does NOT reject on 422. Without this gate, .then(r => r.json())
     parses Shopify's error body happily and the buy path redirects to a
     checkout that never got the item. Surfaces the real reason ("The product
     ... is already sold out") instead of a generic "Error". */
  function thenJson(r) {
    if (r.ok) return r.json();
    return r.text().then(function (t) {
      var body = null;
      try { body = JSON.parse(t); } catch (e) { /* not JSON; fall through */ }
      var reason = (body && (body.description || body.message)) ||
                   (t && t.slice(0, 140)) ||
                   r.statusText ||
                   'Could not add to cart';
      var err = new Error(reason);
      err.status = r.status;
      throw err;
    });
  }

  var DRAWER_QS = ['cart-drawer', 'cart-drawer-component', 'cart-notification', '[is="cart-drawer"]', '[data-cart-drawer]'];
  function findDrawer() {
    for (var i = 0; i < DRAWER_QS.length; i++) {
      var d = document.querySelector(DRAWER_QS[i]);
      if (d) return d;
    }
    return null;
  }

  /* Ask for the theme's own cart sections so the header count actually updates.
     Without this the drawer opens STALE, which is worse than not opening it. */
  function sectionList(drawer) {
    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      try {
        var names = drawer.getSectionsToRender()
          .map(function (s) { return s.section || s.id; })
          .filter(Boolean);
        if (names.length) return names.join(',');
      } catch (e) { /* fall through to the generic list */ }
    }
    return 'cart-drawer,cart-icon-bubble,cart-notification,cart-live-region-text,cart-drawer-component';
  }

  /* Returns a LIVE drawer node. Dawn's <cart-drawer> owns its own rendering, so
     prefer renderContents. Otherwise graft — but the graft replaces the
     children of #shopify-section-cart-drawer, which DESTROYS the node the
     caller is holding, so it must re-query. Opening the detached original is a
     silent no-op: the drawer never appears and, on themes that scroll-lock from
     the open handler, the page can lock behind nothing. */
  function refreshCart(drawer, data) {
    if (!data || !data.sections) return drawer;
    if (drawer && typeof drawer.renderContents === 'function') {
      try {
        drawer.renderContents(data);
        return drawer;
      } catch (e) { /* fall through to the graft */ }
    }
    graftSections(data);
    return findDrawer();
  }

  function graftSections(data) {
    if (!data || !data.sections) return;
    for (var key in data.sections) {
      if (!Object.prototype.hasOwnProperty.call(data.sections, key)) continue;
      var id = 'shopify-section-' + key;
      var target = document.getElementById(id);
      var html = data.sections[key];
      if (!target || !html) continue;
      var parsed = new DOMParser().parseFromString(html, 'text/html').querySelector('#' + id);
      if (!parsed) continue;
      while (target.firstChild) target.removeChild(target.firstChild);
      while (parsed.firstChild) target.appendChild(parsed.firstChild);
    }
  }

  /* Themes vary wildly. Modern Dawn exposes .open(); some forks .openDrawer();
     Horizon exposes `open` as a PROPERTY setter (which a typeof === 'function'
     check silently skips); older Dawn/Brooklyn use a <details> open attribute. */
  function openDrawer(drawer) {
    if (!drawer) return;
    if (typeof drawer.open === 'function') { try { drawer.open(); return; } catch (e) {} }
    if (typeof drawer.show === 'function') { try { drawer.show(); return; } catch (e) {} }
    if (typeof drawer.openDrawer === 'function') { try { drawer.openDrawer(); return; } catch (e) {} }
    if ('open' in drawer && typeof drawer.open !== 'function') { try { drawer.open = true; return; } catch (e) {} }
    if (drawer.tagName === 'DETAILS') { drawer.setAttribute('open', ''); return; }
    drawer.classList.add('active', 'is-active', 'open', 'is-open');
    drawer.setAttribute('aria-hidden', 'false');
  }

  /* ── Cart notify ────────────────────────────────────────────────────
     Grafting the theme's cart sections (refreshCart, above) is only the
     DAWN half of the contract. Per Shopify's own docs, a theme is
     recognised one of two ways: Dawn-style, via an element whose prototype
     has getSectionsToRender() — which is what refreshCart serves — or
     Horizon-style, via a cart-icon with renderCartBubble() and cart-items
     with updateQuantity(), which are driven by a `cart:update` event
     dispatched on `document`. We never sent that event, so on Horizon and
     its forks the badge kept its render-time number and the drawer showed
     page-load HTML until a reload. That is the reported symptom.

     itemCount is read from /cart.js, NOT summed from the add response.
     /cart/add.js returns "the line items associated with the added items"
     and carries no item_count, so summing it yields how many were just
     added — a shopper holding 3 items who adds 1 would have the badge
     REWRITTEN to 1. /cart.js is the only endpoint that reports the true
     total. One small GET, and it is the difference between a correct badge
     and a confidently wrong one.

     The event fires either way: if the read fails we send it with a null
     count, which still tells a listening component to re-render itself. */
  function notifyCart() {
    /* Ours (modulo-block-atc-style listens) plus the generic name themes use. */
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    document.dispatchEvent(new CustomEvent('cart:updated'));
    if (window.Shopify && window.Shopify.PubSub) {
      try { window.Shopify.PubSub.publish('cart-update', {}); } catch (e) {}
    }
    function fire(count) {
      try {
        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: { data: { itemCount: count, source: 'modulo-product-quickbuy' } }
        }));
      } catch (e) {}
    }
    fetch(root() + 'cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (c) { fire(c && typeof c.item_count === 'number' ? c.item_count : null); })
      .catch(function () { fire(null); });
  }

  /* The real label is stashed on first use and never re-read from the DOM.
     The card's busy flag is released as soon as the request settles, but the
     flash runs for another ~1.8s — so a second click lands mid-flash. Reading
     textContent again there would capture "Added" as the original, and the
     button would then read "Added" forever. Clearing the pending timer stops
     the first flash from restoring on top of the second. */
  function flash(button, text, ms, announce) {
    var label = button.querySelector('.modulo-pqb__btn-label');
    if (!label) return;
    if (!button.dataset.pqbLabel) button.dataset.pqbLabel = label.textContent;
    var original = button.dataset.pqbLabel;
    if (button.pqbTimer) clearTimeout(button.pqbTimer);
    label.textContent = text;

    /* The visual result is a label swap on the button the shopper is standing
       on, which is not reliably announced as an accessible-name change. Mirror
       every flash — success AND error — into the section's live region so the
       outcome is spoken, not just seen.

       `announce` is the UNTRUNCATED reason. The label is clipped to 32 chars to
       fit a half-card button, which lands mid-word ("The product 'Power Bank' is
       alre"); a live region has no width to run out of, so speaking the clipped
       string would throw away the half of the sentence that says what went wrong.
       textContent, never innerHTML: this is Shopify's own error text. */
    var sectionEl = button.closest('[data-modulo-pqb]');
    var live = sectionEl && sectionEl.querySelector('[data-modulo-pqb-live]');
    if (live) live.textContent = announce || text;

    button.pqbTimer = setTimeout(function () {
      label.textContent = original;
      button.classList.remove('is-added');
      /* Cleared with the label: a stale "Added" left sitting in the region gets
         re-read by a screen reader on the next unrelated focus change. */
      if (live) live.textContent = '';
      button.pqbTimer = null;
    }, ms);
  }

  /* The in-flight flag lives on the CARD, not the button. A per-button flag
     cannot see its sibling, so a two-button card would let a shopper fire two
     concurrent adds and buy quantity 2 — and on the checkout path, check out
     with it, with no cart page in the flow to catch it. Serializing here also
     keeps us clear of the HTTP 409 Shopify returns for simultaneous cart
     requests (changelog 2025-10-10). */
  function claim(card) {
    if (!card || card.dataset.pqbBusy === '1') return false;
    card.dataset.pqbBusy = '1';
    return true;
  }
  function release(card) {
    if (card) card.dataset.pqbBusy = '0';
  }

  function post(variantId, extra) {
    var body = { items: [{ id: variantId, quantity: 1 }] };
    if (extra) {
      body.sections = extra.sections;
      body.sections_url = extra.sections_url;
    }
    return fetch(root() + 'cart/add.js', {
      method: 'POST',
      // X-Modulo-Internal opts out of the bundle block's in-flight fetch shim.
      // Without it a quick-add landing inside that window is answered with a
      // synthetic 200 {"items":[]} and takes the SUCCESS path on an add that
      // never happened.
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Modulo-Internal': 'product-quickbuy' },
      body: JSON.stringify(body)
    }).then(thenJson);
  }

  function addToCart(button, card) {
    if (!claim(card)) return;
    var drawer = findDrawer();

    post(button.getAttribute('data-variant-id'), {
      sections: sectionList(drawer),
      sections_url: window.location.pathname
    })
      .then(function (data) {
        button.classList.add('is-added');
        flash(button, button.getAttribute('data-added-label') || 'Added', 1800);
        /* Re-bind: refreshCart may have replaced the node `drawer` points at. */
        var live = refreshCart(drawer, data);
        notifyCart();
        openDrawer(live);
      })
      .catch(function (err) {
        var reason = (err && err.message) ? err.message : 'Try again';
        flash(button, reason.slice(0, 32), 2600, reason);
      })
      .then(function () { release(card); });
  }

  function buyNow(button, card) {
    if (!claim(card)) return;

    /* No sections, no drawer, no event dispatch on this path: the navigation
       tears all of it down mid-animation and cancels the section-render fetch. */
    post(button.getAttribute('data-variant-id'))
      .then(function () {
        /* Redirect ONLY here, inside the success branch. Redirecting on error
           lands the shopper at a checkout without the item they asked for. */
        window.location.assign(root() + 'checkout');
      })
      .catch(function (err) {
        release(card);
        var reason = (err && err.message) ? err.message : 'Try again';
        flash(button, reason.slice(0, 32), 2600, reason);
      });
    /* No release() on success — the page is navigating away, and clearing the
       flag would re-open the double-click window during the unload. */
  }

  function initSection(sectionEl) {
    /* Per-element flag, not an IIFE-wide early return: a second instance added
       in the theme editor must still bind. */
    if (sectionEl.dataset.pqbReady === '1') return;
    sectionEl.dataset.pqbReady = '1';

    sectionEl.addEventListener('click', function (e) {
      var add = e.target.closest('[data-modulo-pqb-add]');
      var buy = add ? null : e.target.closest('[data-modulo-pqb-buy]');
      var button = add || buy;
      if (!button || button.disabled) return;
      if (!sectionEl.contains(button)) return;
      e.preventDefault();
      var card = button.closest('[data-modulo-pqb-card]');
      if (add) addToCart(button, card);
      else buyNow(button, card);
    });
  }

  function bootstrap() {
    var nodes = document.querySelectorAll('[data-modulo-pqb]');
    for (var i = 0; i < nodes.length; i++) initSection(nodes[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }

  /* Document-level listeners are registered ONCE. The <script> tag is emitted
     per section instance, so two grids on a page execute this IIFE twice —
     without the guard, every document listener would be double-registered.
     The guard is deliberately scoped to the listeners ONLY: per-element init
     stays keyed on dataset.pqbReady, so a second instance still binds. (The
     siblings' IIFE-wide early return is what leaves editor-added sections dead
     for good.) */
  if (!window.__moduloPqbBound) {
    window.__moduloPqbBound = true;

    /* Without this, a section added in the theme editor stays dead until a full
       page reload. Both section siblings miss it; that is their gap, not the
       house standard. */
    document.addEventListener('shopify:section:load', bootstrap);

    /* buyNow deliberately leaves the card's busy flag set while the page
       navigates to checkout. Back-navigation restores the DOM verbatim from
       bfcache — flag included — which would strand every card the shopper
       tried, with no way to recover short of a hard reload. */
    window.addEventListener('pageshow', function (e) {
      if (!e.persisted) return;
      var cards = document.querySelectorAll('[data-modulo-pqb-card]');
      for (var i = 0; i < cards.length; i++) cards[i].dataset.pqbBusy = '0';
    });
  }
})();
